import { API_ENDPOINTS } from 'Constants';
import type { bookingType } from 'Models/Enums';

export interface UserDto {
	id: string;
	email: string;
	name: string;
	companyName?: string;
	isGuest: boolean;
}

export interface BookingDto {
	id: string;
	humanReadableId: string;
	bookingType: bookingType;
}

/**
 * A charter as the search panel draws it.
 *
 * Not a `BookingDto`: a charter has no booking type, and what it is called is worth showing beside
 * its reference — two charters on the same ferry are told apart by one or the other.
 */
export interface CharterSearchDto {
	id: string;
	humanReadableId?: string;
	name?: string;
	ferryName?: string;
	status: 'Reserved' | 'Booked' | 'Cancelled';
}

// Wire-format discriminator matches SearchUsersEvent.Type on the server. Each panel
// arrives independently as its sub-query completes; the users panel may be followed
// later by a 'users-exact-count' event when the fast-path count was capped at 50.
export type SearchEvent =
	| { type: 'users'; items: UserDto[]; count: number; hasMore: boolean }
	| { type: 'bookings'; items: BookingDto[]; count: number }
	| { type: 'parking'; items: BookingDto[]; count: number }
	| { type: 'charters'; items: CharterSearchDto[]; count: number }
	| { type: 'users-exact-count'; count: number };

/**
 * Streams search events from the backend NDJSON endpoint. Each yielded value is one
 * decoded SearchEvent; the iterator completes when the server closes the stream (all
 * producers settled). Pass an AbortSignal so an in-flight search can be cancelled
 * when the user types another character.
 */
export async function* userSearchFetch(searchText: string, signal?: AbortSignal): AsyncIterable<SearchEvent> {
	const response = await fetch(`${API_ENDPOINTS.USER.GET_SEARCH}?searchText=${encodeURIComponent(searchText)}`, {
		credentials: 'include',
		signal,
	});

	if (!response.ok || !response.body) {
		return;
	}

	const reader = response.body.getReader();
	const decoder = new TextDecoder();
	let buffer = '';

	try {
		while (true) {
			// eslint-disable-next-line no-await-in-loop
			const { done, value } = await reader.read();
			if (done) {
				break;
			}
			buffer += decoder.decode(value, { stream: true });

			let newlineIndex = buffer.indexOf('\n');
			while (newlineIndex !== -1) {
				const line = buffer.slice(0, newlineIndex).trim();
				buffer = buffer.slice(newlineIndex + 1);
				if (line) {
					yield JSON.parse(line) as SearchEvent;
				}
				newlineIndex = buffer.indexOf('\n');
			}
		}
		// Drain any trailing payload that wasn't newline-terminated (servers can omit
		// the final '\n'; we still want the event).
		const tail = buffer.trim();
		if (tail) {
			yield JSON.parse(tail) as SearchEvent;
		}
	} finally {
		try {
			reader.releaseLock();
		} catch {
			// ignore — already released or stream cancelled
		}
	}
}
